import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  Button,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Paper,
  CircularProgress,
  Switch,
  FormControlLabel,
  Snackbar,
  Checkbox,
  ListItemText,
  Breadcrumbs,
  Link,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Refresh as RefreshIcon,
  ManageAccounts as ManageIcon,
  LockReset as ResetPasswordIcon,
  VerifiedUser as VerifiedIcon,
  Cancel as CancelIcon,
  LocationOn as LocationIcon,
  Home as HomeIcon,
  NavigateNext as NavigateNextIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import api from '../services/auth';
import { getCurrentUser } from '../services/auth';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';

const ManageUser = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [dialogMode, setDialogMode] = useState('add');
  const [entreprises, setEntreprises] = useState([]);
  const [succursales, setSuccursales] = useState([]);
  const [filteredSuccursales, setFilteredSuccursales] = useState([]);
  const [loadingSuccursales, setLoadingSuccursales] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [selectedEntreprise, setSelectedEntreprise] = useState(null);
  const [initialLoadDone, setInitialLoadDone] = useState(false);

  const [succursaleFilter, setSuccursaleFilter] = useState('');
  const [allSuccursales, setAllSuccursales] = useState([]);
  const [showSuccursaleFilter, setShowSuccursaleFilter] = useState(false);

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    password2: '',
    email: '',
    first_name: '',
    last_name: '',
    role: 'vendeur',
    entreprise: '',
    succursale_principale: '',
    succursales_ids: [],
    telephone: ''
  });

  const [formErrors, setFormErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);

  const getAvailableRoles = () => {
    if (!currentUser) return [];
    const roles = [
      // { value: 'vendeur', label: 'Vendeur' },
      { value: 'caissier', label: 'Caissier' },
      { value: 'manager', label: 'Manager' }
    ];
    if (currentUser.role === 'super_admin') {
      roles.push(
        { value: 'proprietaire', label: 'Propriétaire' },
        { value: 'super_admin', label: 'Super Admin' }
      );
    }
    return roles;
  };

  useEffect(() => {
    const loadCurrentUser = async () => {
      const user = getCurrentUser();
      setCurrentUser(user);
      if (!user) {
        window.location.href = '/login';
        return;
      }
      setInitialLoadDone(true);
    };
    loadCurrentUser();
  }, []);

  useEffect(() => {
    if (currentUser && initialLoadDone) {
      fetchUsers();
      fetchAllSuccursales();
    }
  }, [currentUser, initialLoadDone]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const fetchAllSuccursales = async () => {
    try {
      const response = await api.get('/succursales/');
      let data = [];
      if (Array.isArray(response.data)) data = response.data;
      else if (response.data?.results) data = response.data.results;
      else if (response.data?.data) data = response.data.data;

      let filteredData = data;
      if (currentUser?.role === 'manager') {
        filteredData = data.filter(s => s.id === currentUser.succursale_principale);
      } else if (currentUser?.role === 'proprietaire') {
        filteredData = data.filter(s => {
          const succursaleEntrepriseId = s.entreprise?.id || s.entreprise;
          return succursaleEntrepriseId == currentUser.entreprise;
        });
      }
      setAllSuccursales(filteredData);
    } catch (err) {
      console.error('Erreur chargement succursales:', err);
      setAllSuccursales([]);
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('users/');
      let data = [];
      if (Array.isArray(response.data)) data = response.data;
      else if (response.data?.results) data = response.data.results;
      else if (response.data?.data) data = response.data.data;

      const filteredUsers = data.filter(user => {
        if (!currentUser) return false;
        if (currentUser.role === 'super_admin') return true;
        if (currentUser.role === 'proprietaire') return user.entreprise === currentUser.entreprise;
        if (currentUser.role === 'manager') {
          return user.succursale_principale === currentUser.succursale_principale ||
            user.succursales?.includes(currentUser.succursale_principale);
        }
        return false;
      });

      setUsers(filteredUsers);
      setError('');
    } catch (err) {
      console.error('Erreur utilisateurs:', err);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSuccursalesByEntreprise = async (entrepriseId) => {
    if (!entrepriseId) {
      setFilteredSuccursales([]);
      setSuccursales([]);
      return;
    }
    try {
      setLoadingSuccursales(true);
      let response;
      if (currentUser?.role === 'super_admin') {
        response = await api.get('/succursales/', { params: { entreprise: entrepriseId } });
      } else {
        response = await api.get('/succursales/');
      }

      let allSuccursalesData = [];
      if (Array.isArray(response.data)) allSuccursalesData = response.data;
      else if (response.data?.results) allSuccursalesData = response.data.results;
      else if (response.data?.data) allSuccursalesData = response.data.data;

      setSuccursales(allSuccursalesData);

      const filtered = allSuccursalesData.filter(s => {
        const succursaleEntrepriseId = s.entreprise?.id || s.entreprise;
        return succursaleEntrepriseId == entrepriseId;
      });

      setFilteredSuccursales(filtered);

      if (formData.succursale_principale && filtered.length > 0) {
        const currentSuccursaleId = parseInt(formData.succursale_principale);
        if (!filtered.some(s => s.id === currentSuccursaleId)) {
          setFormData(prev => ({ ...prev, succursale_principale: '', succursales_ids: [] }));
        }
      }

      if (filtered.length === 0 && selectedEntreprise) {
        showSnackbar(`Aucune succursale trouvée pour ${selectedEntreprise.nom}`, 'warning');
      }
    } catch (err) {
      console.error('Erreur succursales:', err.response?.data || err);
      setSuccursales([]);
      setFilteredSuccursales([]);
      showSnackbar('Erreur lors du chargement des succursales', 'error');
    } finally {
      setLoadingSuccursales(false);
    }
  };

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const handleSuccursaleFilterChange = (event) => {
    setSuccursaleFilter(event.target.value);
    setPage(0);
  };

  const toggleSuccursaleFilter = () => {
    setShowSuccursaleFilter(!showSuccursaleFilter);
    if (showSuccursaleFilter) setSuccursaleFilter('');
  };

  const clearFilters = () => {
    setSearch('');
    setSuccursaleFilter('');
  };

  const handleOpenDialog = (mode, user = null) => {
    setDialogMode(mode);
    setSelectedUser(user);
    setFormErrors({});
    setSelectedEntreprise(null);

    if (mode === 'edit' || mode === 'view') {
      const entrepriseId = user.entreprise?.id || user.entreprise;
      const entreprise = entreprises.find(e => e.id === entrepriseId);
      setSelectedEntreprise(entreprise);
      setFormData({
        username: user.username || '',
        password: '',
        password2: '',
        email: user.email || '',
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        role: user.role || 'vendeur',
        entreprise: entrepriseId || '',
        succursale_principale: user.succursale_principale?.id || user.succursale_principale || '',
        succursales_ids: user.succursales?.map(s => s.id) || user.succursales || [],
        telephone: user.telephone || ''
      });
      if (entrepriseId) fetchSuccursalesByEntreprise(entrepriseId);
    } else {
      const newFormData = {
        username: '', password: '', password2: '', email: '',
        first_name: '', last_name: '', role: 'vendeur',
        entreprise: '', succursale_principale: '', succursales_ids: [], telephone: ''
      };
      if (currentUser && currentUser.role !== 'super_admin' && currentUser.entreprise) {
        const entreprise = entreprises.find(e => e.id === currentUser.entreprise);
        setSelectedEntreprise(entreprise);
        newFormData.entreprise = currentUser.entreprise;
        fetchSuccursalesByEntreprise(currentUser.entreprise);
      }
      setFormData(newFormData);
      setFilteredSuccursales([]);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setFormErrors({});
    setShowPassword(false);
    setSelectedEntreprise(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'entreprise') {
      const entreprise = entreprises.find(e => e.id === value);
      setSelectedEntreprise(entreprise);
      fetchSuccursalesByEntreprise(value);
      setFormData(prev => ({ ...prev, [name]: value, succursale_principale: '', succursales_ids: [] }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSuccursalesChange = (event) => {
    const { value } = event.target;
    setFormData(prev => ({ ...prev, succursales_ids: value }));
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.username?.trim()) errors.username = "Nom d'utilisateur requis";
    if (dialogMode === 'add' && !formData.password) errors.password = 'Mot de passe requis';
    if (dialogMode === 'add' && formData.password && formData.password.length < 4)
      errors.password = 'Le mot de passe doit contenir au moins 4 caractères';
    if (dialogMode === 'add' && formData.password !== formData.password2)
      errors.password2 = 'Les mots de passe ne correspondent pas';
    if (!formData.role) errors.role = 'Rôle requis';
    if (!formData.entreprise) errors.entreprise = 'Entreprise requise';
    if (!formData.succursale_principale) errors.succursale_principale = 'Succursale principale obligatoire';
    if (formData.role === 'super_admin' && currentUser?.role !== 'super_admin')
      errors.role = 'Seul un Super Admin peut créer un Super Admin';
    if (formData.role === 'proprietaire' && currentUser?.role !== 'super_admin')
      errors.role = 'Seul un Super Admin peut créer un Propriétaire';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email))
      errors.email = 'Email invalide';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      showSnackbar('Veuillez corriger les erreurs dans le formulaire', 'error');
      return;
    }
    setActionLoading(true);
    try {
      const dataToSend = {
        ...formData,
        entreprise: parseInt(formData.entreprise),
        succursale_principale: parseInt(formData.succursale_principale),
        succursales_ids: formData.succursales_ids.map(id => parseInt(id))
      };
      if (dialogMode === 'edit' && !dataToSend.password) {
        delete dataToSend.password;
        delete dataToSend.password2;
      }
      if (dialogMode === 'add') {
        await api.post('/users/', dataToSend);
        showSnackbar('Utilisateur créé avec succès', 'success');
      } else if (dialogMode === 'edit') {
        await api.put(`/users/${selectedUser.id}/`, dataToSend);
        showSnackbar('Utilisateur modifié avec succès', 'success');
      }
      await fetchUsers();
      handleCloseDialog();
    } catch (err) {
      console.error('Erreur enregistrement:', err.response?.data || err);
      if (err.response?.status === 400) {
        const apiErrors = err.response.data;
        if (apiErrors.succursale_principale) {
          setFormErrors(prev => ({
            ...prev,
            succursale_principale: Array.isArray(apiErrors.succursale_principale)
              ? apiErrors.succursale_principale.join(', ')
              : apiErrors.succursale_principale
          }));
        }
        const otherErrors = { ...apiErrors };
        delete otherErrors.succursale_principale;
        setFormErrors(otherErrors);
        showSnackbar('Erreurs de validation, vérifiez le formulaire', 'error');
      } else if (err.response?.status === 403) {
        showSnackbar('Permission refusée', 'error');
      } else {
        showSnackbar("Erreur lors de l'enregistrement", 'error');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet utilisateur ?')) return;
    setActionLoading(true);
    try {
      await api.delete(`/users/${id}/`);
      showSnackbar('Utilisateur supprimé avec succès', 'success');
      await fetchUsers();
    } catch (err) {
      showSnackbar('Erreur lors de la suppression', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResetPassword = async (id) => {
    if (!window.confirm('Réinitialiser le mot de passe de cet utilisateur ?')) return;
    setActionLoading(true);
    try {
      await api.post(`/users/${id}/reset-password/`);
      showSnackbar('Mot de passe réinitialisé avec succès', 'success');
    } catch (err) {
      showSnackbar('Erreur lors de la réinitialisation', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleActive = async (id, isActive) => {
    setActionLoading(true);
    try {
      await api.patch(`/users/${id}/`, { is_active: !isActive });
      await fetchUsers();
      showSnackbar(`Utilisateur ${!isActive ? 'activé' : 'désactivé'} avec succès`, 'success');
    } catch (err) {
      showSnackbar('Erreur lors du changement de statut', 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchUsers();
    fetchAllSuccursales();
    showSnackbar('Données actualisées', 'info');
  };

  const canCreateUser = () => {
    if (!currentUser) return false;
    return ['super_admin', 'proprietaire', 'manager'].includes(currentUser.role);
  };

  const canEditUser = (user) => {
    if (!currentUser) return false;
    if (currentUser.role === 'super_admin') return true;
    if (['proprietaire', 'manager'].includes(currentUser.role) && user.entreprise === currentUser.entreprise) return true;
    return false;
  };

  const canDeleteUser = (user) => {
    if (!currentUser) return false;
    if (user.role === 'super_admin') return false;
    if (currentUser.role === 'super_admin') return true;
    if (currentUser.role === 'proprietaire' && user.entreprise === currentUser.entreprise && user.role !== 'super_admin') return true;
    return false;
  };

  const safeUsers = Array.isArray(users) ? users : [];

  const filteredUsers = safeUsers.filter(user => {
    if (!user) return false;
    const searchLower = search.toLowerCase();
    const matchesSearch = (
      (user.username?.toLowerCase() || '').includes(searchLower) ||
      (user.email?.toLowerCase() || '').includes(searchLower) ||
      (user.first_name?.toLowerCase() || '').includes(searchLower) ||
      (user.last_name?.toLowerCase() || '').includes(searchLower) ||
      (user.role?.toLowerCase() || '').includes(searchLower) ||
      (user.telephone?.toLowerCase() || '').includes(searchLower)
    );
    if (!matchesSearch) return false;
    if (succursaleFilter) {
      const userSuccursaleId = user.succursale_principale?.id || user.succursale_principale;
      const userSuccursaleIds = user.succursales?.map(s => s.id) || user.succursales || [];
      return userSuccursaleId == succursaleFilter || userSuccursaleIds.includes(parseInt(succursaleFilter));
    }
    return true;
  });

  const paginatedUsers = filteredUsers.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      return format(parseISO(dateString), 'dd/MM/yyyy', { locale: fr });
    } catch (e) {
      return dateString;
    }
  };

  const getRoleLabel = (role) => {
    const roleLabels = {
      'super_admin': 'Super Admin', 'proprietaire': 'Propriétaire',
      'manager': 'Manager', 'vendeur': 'Vendeur', 'caissier': 'Caissier'
    };
    return roleLabels[role] || role;
  };

  const getRoleColor = (role) => {
    const roleColors = {
      'super_admin': 'error', 'proprietaire': 'warning',
      'manager': 'info', 'vendeur': 'success', 'caissier': 'primary'
    };
    return roleColors[role] || 'default';
  };

  const getSuccursaleName = (id) => {
    const succursale = allSuccursales.find(s => s.id === id);
    return succursale ? succursale.nom : 'N/A';
  };

  if (!initialLoadDone || (loading && !currentUser)) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Chargement...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      {/* Breadcrumb */}
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" />}
        sx={{ mb: 2, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
      >
        <Link href="/dashboard" underline="hover" color="inherit" sx={{ display: 'flex', alignItems: 'center' }}>
          <HomeIcon sx={{ mr: 0.5, fontSize: { xs: 16, sm: 20 } }} />
          {!isMobile && 'Dashboard'}
        </Link>
        <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center', fontSize: 'inherit' }}>
          <ManageIcon sx={{ mr: 0.5, fontSize: { xs: 16, sm: 20 }, verticalAlign: 'bottom' }} />
          {isMobile ? 'Utilisateurs' : 'Gestion des Utilisateurs'}
        </Typography>
      </Breadcrumbs>

      <Card sx={{ p: { xs: 1.5, sm: 2, md: 3 }, mb: 3, boxShadow: 3, borderRadius: 2 }}>
        {/* Header responsive */}
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: 3
        }}>
          <Box>
            <Typography variant={isMobile ? 'h6' : 'h5'} component="h1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              Gestion des Utilisateurs
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {filteredUsers.length} utilisateur(s) trouvé(s)
              {succursaleFilter && ` • ${getSuccursaleName(parseInt(succursaleFilter))}`}
            </Typography>
          </Box>

          <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            justifyContent: { xs: 'stretch', sm: 'flex-end' }
          }}>
            <Tooltip title="Filtrer par succursale">
              <Button
                variant={showSuccursaleFilter ? 'contained' : 'outlined'}
                startIcon={<FilterIcon />}
                onClick={toggleSuccursaleFilter}
                color={showSuccursaleFilter ? 'primary' : 'inherit'}
                size={isMobile ? 'small' : 'medium'}
                fullWidth={isMobile}
                sx={{ borderRadius: '8px' }}
              >
                {showSuccursaleFilter ? 'Masquer' : 'Filtrer'}
              </Button>
            </Tooltip>

            <Tooltip title="Actualiser">
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={actionLoading || loading}
                size={isMobile ? 'small' : 'medium'}
                fullWidth={isMobile}
                sx={{ borderRadius: '8px' }}
              >
                Actualiser
              </Button>
            </Tooltip>

            {canCreateUser() && (
              <Tooltip title="Ajouter un nouvel utilisateur">
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleOpenDialog('add')}
                  disabled={actionLoading}
                  size={isMobile ? 'small' : 'medium'}
                  fullWidth={isMobile}
                  sx={{ borderRadius: '8px' }}
                >
                  {isMobile ? 'Ajouter' : 'Nouvel Utilisateur'}
                </Button>
              </Tooltip>
            )}
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Barre de recherche + filtre */}
        <Box mb={3}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={showSuccursaleFilter ? 8 : 12}>
              <TextField
                fullWidth
                variant="outlined"
                placeholder={isMobile ? 'Rechercher...' : 'Rechercher par nom, email, prénom, rôle, téléphone...'}
                value={search}
                onChange={handleSearch}
                disabled={actionLoading}
                size={isMobile ? 'small' : 'medium'}
                sx={{
                  '& .MuiOutlinedInput-root': { borderRadius: '8px' }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize={isMobile ? 'small' : 'medium'} />
                    </InputAdornment>
                  ),
                  endAdornment: (search || succursaleFilter) && (
                    <InputAdornment position="end">
                      <Tooltip title="Effacer les filtres">
                        <IconButton size="small" onClick={clearFilters} disabled={actionLoading}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>

            {showSuccursaleFilter && (
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size={isMobile ? 'small' : 'medium'}>
                  <InputLabel>Succursale</InputLabel>
                  <Select
                    value={succursaleFilter}
                    onChange={handleSuccursaleFilterChange}
                    label="Succursale"
                  >
                    <MenuItem value="">Toutes les succursales</MenuItem>
                    {allSuccursales.map((succursale) => (
                      <MenuItem key={succursale.id} value={succursale.id}>
                        <Box display="flex" alignItems="center">
                          <LocationIcon sx={{ mr: 1, fontSize: 18, color: 'text.secondary' }} />
                          {succursale.nom}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </Box>

        {/* Tableau avec scroll horizontal sur mobile */}
        <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 2 }}>
          <Box sx={{ overflowX: 'auto' }}>
            <Table sx={{ minWidth: isMobile ? 500 : 650 }}>
              <TableHead sx={{ bgcolor: 'primary.main' }}>
                <TableRow>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Utilisateur</TableCell>
                  {!isMobile && <TableCell sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Email/Tél.</TableCell>}
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Rôle</TableCell>
                  {!isMobile && <TableCell sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Succursale</TableCell>}
                  {!isTablet && <TableCell sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Créé le</TableCell>}
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Statut</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold', whiteSpace: 'nowrap' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={isMobile ? 4 : 7} align="center" sx={{ py: 4 }}>
                      <Box display="flex" justifyContent="center" alignItems="center">
                        <CircularProgress size={24} sx={{ mr: 2 }} />
                        <Typography color="text.secondary">Chargement...</Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : paginatedUsers.length > 0 ? (
                  paginatedUsers.map((user) => {
                    const isItemSelected = false;
                    return (
                      <TableRow
                        hover
                        key={user.id}
                        tabIndex={-1}
                        sx={{ opacity: user.is_active !== false ? 1 : 0.7 }}
                      >
                        <TableCell sx={{ py: { xs: 1, sm: 1.5 } }}>
                          <Box display="flex" alignItems="center">
                            <PersonIcon sx={{ mr: 1, color: 'text.secondary', fontSize: { xs: 18, sm: 24 } }} />
                            <Box>
                              <Typography variant="body2" fontWeight="medium" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                {user.first_name && user.last_name
                                  ? `${user.first_name} ${user.last_name}`
                                  : user.username}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                @{user.username}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>

                        {!isMobile && (
                          <TableCell>
                            <Typography variant="body2" gutterBottom sx={{ fontSize: '0.8rem' }}>
                              {user.email || 'N/A'}
                            </Typography>
                            {user.telephone && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                <PhoneIcon sx={{ fontSize: 12, mr: 0.5 }} />
                                {user.telephone}
                              </Typography>
                            )}
                          </TableCell>
                        )}

                        <TableCell sx={{ py: { xs: 1, sm: 1.5 } }}>
                          <Chip
                            label={getRoleLabel(user.role)}
                            color={getRoleColor(user.role)}
                            size="small"
                            sx={{ fontWeight: 'medium', fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                          />
                        </TableCell>

                        {!isMobile && (
                          <TableCell>
                            {user.succursale_principale_detail ? (
                              <Box>
                                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                  {user.succursale_principale_detail.nom}
                                </Typography>
                                {user.succursales_count > 1 && (
                                  <Typography variant="caption" color="text.secondary" display="block">
                                    +{user.succursales_count - 1} autre(s)
                                  </Typography>
                                )}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                {getSuccursaleName(user.succursale_principale) || 'N/A'}
                              </Typography>
                            )}
                          </TableCell>
                        )}

                        {!isTablet && (
                          <TableCell>
                            <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                              {formatDate(user.date_joined || user.date_creation)}
                            </Typography>
                          </TableCell>
                        )}

                        <TableCell sx={{ py: { xs: 1, sm: 1.5 } }}>
                          <Chip
                            icon={user.is_active !== false ? <VerifiedIcon /> : <CancelIcon />}
                            label={isMobile
                              ? (user.is_active !== false ? '✓' : '✗')
                              : (user.is_active !== false ? 'Actif' : 'Inactif')}
                            color={user.is_active !== false ? 'success' : 'error'}
                            size="small"
                            onClick={() => handleToggleActive(user.id, user.is_active)}
                            sx={{ cursor: canEditUser(user) ? 'pointer' : 'default', fontWeight: 'medium' }}
                            disabled={actionLoading || !canEditUser(user)}
                          />
                        </TableCell>

                        <TableCell sx={{ py: { xs: 1, sm: 1.5 } }}>
                          <Box display="flex" gap={0.5}>
                            <Tooltip title="Voir détails">
                              <IconButton
                                size="small"
                                onClick={() => handleOpenDialog('view', user)}
                                color="info"
                                disabled={actionLoading}
                                sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'info.main', color: 'white' }, p: { xs: 0.5, sm: 1 } }}
                              >
                                <ViewIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />
                              </IconButton>
                            </Tooltip>

                            {canEditUser(user) && (
                              <Tooltip title="Modifier">
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenDialog('edit', user)}
                                  color="primary"
                                  disabled={actionLoading}
                                  sx={{ bgcolor: 'secondary.light', '&:hover': { bgcolor: 'primary.main', color: 'white' }, p: { xs: 0.5, sm: 1 } }}
                                >
                                  <EditIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />
                                </IconButton>
                              </Tooltip>
                            )}

                            {canEditUser(user) && !isMobile && (
                              <Tooltip title="Réinitialiser mot de passe">
                                <IconButton
                                  size="small"
                                  onClick={() => handleResetPassword(user.id)}
                                  color="warning"
                                  disabled={actionLoading}
                                  sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'warning.main', color: 'white' }, p: { xs: 0.5, sm: 1 } }}
                                >
                                  <ResetPasswordIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />
                                </IconButton>
                              </Tooltip>
                            )}

                            {canDeleteUser(user) && (
                              <Tooltip title="Supprimer">
                                <IconButton
                                  size="small"
                                  onClick={() => handleDelete(user.id)}
                                  color="error"
                                  disabled={actionLoading}
                                  sx={{ bgcolor: 'secondary.light', '&:hover': { bgcolor: 'error.main', color: 'white' }, p: { xs: 0.5, sm: 1 } }}
                                >
                                  <DeleteIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={isMobile ? 4 : 7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        {search || succursaleFilter
                          ? 'Aucun utilisateur ne correspond à vos critères'
                          : 'Aucun utilisateur trouvé'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>

          {filteredUsers.length > 0 && (
            <TablePagination
              rowsPerPageOptions={isMobile ? [5, 10] : [5, 10, 25]}
              component="div"
              count={filteredUsers.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage={isMobile ? '' : 'Lignes par page:'}
              labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
              sx={{ '& .MuiTablePagination-toolbar': { flexWrap: isMobile ? 'wrap' : 'nowrap', minHeight: isMobile ? 48 : 52 } }}
            />
          )}
        </Paper>
      </Card>

      {/* Dialog fullScreen sur mobile */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: { borderRadius: isMobile ? 0 : 2 }
        }}
      >
        <DialogTitle sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: { xs: 1.5, sm: 2 },
          px: { xs: 2, sm: 3 }
        }}>
          <Box display="flex" alignItems="center">
            {dialogMode === 'add' ? <AddIcon sx={{ mr: 1 }} /> :
              dialogMode === 'edit' ? <EditIcon sx={{ mr: 1 }} /> :
                <ViewIcon sx={{ mr: 1 }} />}
            <Typography variant={isMobile ? 'subtitle1' : 'h6'} sx={{ fontWeight: 'bold' }}>
              {dialogMode === 'add' ? 'Nouvel Utilisateur' :
                dialogMode === 'edit' ? "Modifier l'Utilisateur" :
                  "Détails de l'Utilisateur"}
            </Typography>
          </Box>
          {selectedUser?.username && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.9 }}>
              @{selectedUser.username}
            </Typography>
          )}
        </DialogTitle>

        <DialogContent sx={{ mt: 1, px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 } }}>
          <Grid container spacing={{ xs: 1.5, sm: 2 }}>
            {selectedEntreprise && (
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, bgcolor: 'grey.50' }}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Entreprise:</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        <BusinessIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'text-bottom' }} />
                        {selectedEntreprise.nom}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle2" color="text.secondary">Succursales dispo:</Typography>
                      <Typography variant="body2" fontWeight="medium">{filteredSuccursales.length}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nom d'utilisateur *"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                error={!!formErrors.username}
                helperText={formErrors.username}
                disabled={dialogMode === 'view' || actionLoading}
                size={isMobile ? 'small' : 'medium'}
                sx={{ mt: { xs: 0, sm: 1 } }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                error={!!formErrors.email}
                helperText={formErrors.email}
                disabled={dialogMode === 'view' || actionLoading}
                size={isMobile ? 'small' : 'medium'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {dialogMode !== 'view' && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Mot de passe *"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={handleInputChange}
                    error={!!formErrors.password}
                    helperText={formErrors.password || (dialogMode === 'edit' ? 'Laisser vide pour ne pas changer' : 'Min. 4 caractères')}
                    disabled={actionLoading}
                    size={isMobile ? 'small' : 'medium'}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Confirmer le mot de passe *"
                    name="password2"
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password2}
                    onChange={handleInputChange}
                    error={!!formErrors.password2}
                    helperText={formErrors.password2}
                    disabled={actionLoading}
                    size={isMobile ? 'small' : 'medium'}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={showPassword}
                        onChange={(e) => setShowPassword(e.target.checked)}
                        color="primary"
                        size={isMobile ? 'small' : 'medium'}
                      />
                    }
                    label={<Typography variant="body2">Afficher le mot de passe</Typography>}
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Prénom"
                name="first_name"
                value={formData.first_name}
                onChange={handleInputChange}
                disabled={dialogMode === 'view' || actionLoading}
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nom"
                name="last_name"
                value={formData.last_name}
                onChange={handleInputChange}
                disabled={dialogMode === 'view' || actionLoading}
                size={isMobile ? 'small' : 'medium'}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Téléphone"
                name="telephone"
                value={formData.telephone}
                onChange={handleInputChange}
                disabled={dialogMode === 'view' || actionLoading}
                size={isMobile ? 'small' : 'medium'}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl
                fullWidth
                error={!!formErrors.role}
                disabled={dialogMode === 'view' || actionLoading}
                size={isMobile ? 'small' : 'medium'}
              >
                <InputLabel>Rôle *</InputLabel>
                <Select name="role" value={formData.role} onChange={handleInputChange} label="Rôle *">
                  {getAvailableRoles().map((role) => (
                    <MenuItem key={role.value} value={role.value}>{role.label}</MenuItem>
                  ))}
                </Select>
                {formErrors.role && (
                  <Typography variant="caption" color="error">{formErrors.role}</Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl
                fullWidth
                error={!!formErrors.entreprise}
                disabled={dialogMode === 'view' || actionLoading || (currentUser && currentUser.role !== 'super_admin')}
                size={isMobile ? 'small' : 'medium'}
              >
                <InputLabel>Entreprise *</InputLabel>
                <Select name="entreprise" value={formData.entreprise} onChange={handleInputChange} label="Entreprise *">
                  {entreprises.map((entreprise) => (
                    <MenuItem key={entreprise.id} value={entreprise.id}>
                      {entreprise.nom}
                    </MenuItem>
                  ))}
                </Select>
                {formErrors.entreprise && (
                  <Typography variant="caption" color="error">{formErrors.entreprise}</Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6}>
              <FormControl
                fullWidth
                error={!!formErrors.succursale_principale}
                disabled={dialogMode === 'view' || actionLoading || loadingSuccursales || !formData.entreprise}
                size={isMobile ? 'small' : 'medium'}
              >
                <InputLabel>Succursale principale *</InputLabel>
                <Select
                  name="succursale_principale"
                  value={formData.succursale_principale}
                  onChange={handleInputChange}
                  label="Succursale principale *"
                >
                  {loadingSuccursales ? (
                    <MenuItem disabled>
                      <CircularProgress size={16} sx={{ mr: 1 }} /> Chargement...
                    </MenuItem>
                  ) : filteredSuccursales.length === 0 ? (
                    <MenuItem disabled>Aucune succursale disponible</MenuItem>
                  ) : (
                    filteredSuccursales.map((succursale) => (
                      <MenuItem key={succursale.id} value={succursale.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <LocationIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} />
                          <Box>
                            <Typography variant="body2">{succursale.nom}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {succursale.code || 'N/A'} • {succursale.ville || 'N/A'}
                            </Typography>
                          </Box>
                        </Box>
                      </MenuItem>
                    ))
                  )}
                </Select>
                {formErrors.succursale_principale ? (
                  <Typography variant="caption" color="error">{formErrors.succursale_principale}</Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    {!formData.entreprise ? "Sélectionnez d'abord une entreprise" :
                      loadingSuccursales ? 'Chargement...' : 'Obligatoire'}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <FormControl
                fullWidth
                disabled={dialogMode === 'view' || actionLoading || loadingSuccursales || !formData.entreprise || !formData.succursale_principale}
                size={isMobile ? 'small' : 'medium'}
              >
                <InputLabel>Succursales supplémentaires</InputLabel>
                <Select
                  multiple
                  name="succursales_ids"
                  value={formData.succursales_ids}
                  onChange={handleSuccursalesChange}
                  label="Succursales supplémentaires"
                  renderValue={(selected) => {
                    const selectedSuccursales = filteredSuccursales.filter(s => selected.includes(s.id));
                    return selectedSuccursales.map(s => s.nom).join(', ') || 'Aucune';
                  }}
                  MenuProps={{ PaperProps: { style: { maxHeight: 250 } } }}
                >
                  {filteredSuccursales
                    .filter(s => s.id !== formData.succursale_principale)
                    .map((succursale) => (
                      <MenuItem key={succursale.id} value={succursale.id}>
                        <Checkbox checked={formData.succursales_ids.indexOf(succursale.id) > -1} size="small" />
                        <ListItemText
                          primary={succursale.nom}
                          secondary={`${succursale.code || 'N/A'} - ${succursale.ville}`}
                        />
                      </MenuItem>
                    ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Optionnel — succursales supplémentaires accessibles
                </Typography>
              </FormControl>
            </Grid>

            {Object.keys(formErrors).filter(key =>
              !['username', 'email', 'password', 'password2', 'role', 'entreprise', 'succursale_principale'].includes(key)
            ).length > 0 && (
              <Grid item xs={12}>
                <Alert severity="error" sx={{ mt: 1 }}>
                  <Typography variant="subtitle2">Erreurs :</Typography>
                  <ul style={{ margin: 0, paddingLeft: '20px' }}>
                    {Object.entries(formErrors)
                      .filter(([key]) => !['username', 'email', 'password', 'password2', 'role', 'entreprise', 'succursale_principale'].includes(key))
                      .map(([key, value]) => (
                        <li key={key}><strong>{key}</strong>: {Array.isArray(value) ? value.join(', ') : value}</li>
                      ))}
                  </ul>
                </Alert>
              </Grid>
            )}
          </Grid>
        </DialogContent>

        <DialogActions sx={{
          p: { xs: 1.5, sm: 2 },
          borderTop: '1px solid rgba(0, 0, 0, 0.12)',
          gap: 1,
          flexDirection: { xs: 'column-reverse', sm: 'row' }
        }}>
          <Button
            onClick={handleCloseDialog}
            disabled={actionLoading}
            variant="outlined"
            fullWidth={isMobile}
            sx={{ borderRadius: '8px' }}
          >
            {dialogMode === 'view' ? 'Fermer' : 'Annuler'}
          </Button>

          {dialogMode !== 'view' && (
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={actionLoading}
              fullWidth={isMobile}
              startIcon={actionLoading ? <CircularProgress size={18} /> : null}
              sx={{ borderRadius: '8px' }}
            >
              {dialogMode === 'add' ? "Créer l'utilisateur" : 'Enregistrer'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: isMobile ? 'center' : 'right' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageUser;
